import _ from "lodash";
import MacroCategoryOrder from "../MacroCategoryOrder";

export const getSortIndex = (category, orderArray) => {
    const lowerCaseCategory = category.toLowerCase();
    // findIndex returns the index of the first element that satisfies the condition
    const index = orderArray?.findIndex((orderItem) => orderItem.toLowerCase().includes(lowerCaseCategory));
    // If a category isn't found, push it to the end by returning a large number
    return index === -1 ? Infinity : index;
};

export const highlightText = (text, query) => {
    const escapeRegex = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    if (!query) return text;
    const safeQuery = escapeRegex(query);
    const regex = new RegExp(`(${safeQuery})`, "gi");
    return text.replace(regex, '<span class="highlight">$1</span>');
};

export const getCustomRound = (n) => {
    if (n >= 0) {
        return Math.floor(n + 0.5);
    } else {
        return Math.ceil(n - 0.5);
    }
};

export const getFormattedScoreOrNA = (score = 0, analysisType = "mean", absoluteToggle = false) => {
    let result = "NA";
    if (score) {
        if (analysisType === "count" || absoluteToggle) {
            result = score;
        } else if (analysisType !== "mean" && analysisType !== "weight_mean") {
            result = getCustomRound(Number(score)) + "%";
        } else {
            result = Number(score).toFixed(2);
        }
    }
    return result;
};

export const getOverallCalculationResult = (total = 0, sampleTotal = 0, totalVisibleProducts = 0, dataViewKey = "mean") => {
    let result = "NA";
    if (total && total > 0 && sampleTotal > 0) {
        if (dataViewKey !== "mean" && dataViewKey !== "weight_mean") {
            result = getCustomRound(Number((total * 100) / sampleTotal)) + "%";
        } else if (totalVisibleProducts !== 0) {
            result = Number(total / totalVisibleProducts).toFixed(2);
        }
    }
    return result;
};

export const getSelectedOption = (type, optionObj) => {
    let label = "";
    if (type === "top") {
        label = optionObj?.top_box?.label;
    } else {
        label = optionObj?.bottom_box?.label;
    }
    const match = label?.match(/^(.*?)\s*(\([^)]*\))$/);
    const optionTitle = (
        <span
            style={{
                marginRight: "10px",
            }}
        >
            {match?.[1]} <span className="option-range">{match?.[2]}</span>
        </span>
    );

    return optionTitle;
};

export const getOralCareProductCodeTableHeader = (datum, productsTestCodeMapData) => {
    const proTitle = `${datum.title}__${datum.coreDataId}`;
    let proTestCode = productsTestCodeMapData[proTitle];
    let title = datum.title;

    if (datum.title.includes(proTestCode)) {
        [title, proTestCode] = datum.title.split("/");
    }
    const code = <span style={{ color: "#6f42c1" }}>{proTestCode}</span>;
    return [title, code];
};

export const getViewKeys = (dataViewKey) => {
    let key1 = null;
    let key2 = null;
    let key3 = null;
    if (dataViewKey) {
        const splitArr = dataViewKey.split(",");

        if (splitArr.length > 1) {
            key1 = splitArr[0];
            key2 = splitArr[1];
            key3 = splitArr[2];
        } else {
            key1 = splitArr[0];
        }
    }

    return [key1, key2, key3];
};

export const getRecallAndITM = (rowDisplayLabel, rowBackendVariable, moduleName, kpis) => {
    if (moduleName === "overall") {
        const filteredBvObj = kpis.filter((ele) => ele.displayLabel === rowDisplayLabel);
        return {
            recallBvObj: filteredBvObj.find((ele) => ele.variableCategory === "KPI (OO)") || {},
            itmBvObj: filteredBvObj.find((ele) => ele.variableCategory === "KPI ITM") || {},
        };
    } else {
        return {
            recallBvObj: kpis.find((ele) => ele.backendVariable === rowBackendVariable) || {},
        };
    }
};



// ==================== CONCEPT/RECALL FUNCTIONS ====================

/**
 * Check if a variable is Concept data
 * Concept variables start with R_C_ or S_C_
 */
export const isConceptVariable = (backendVariable) => {
    if (!backendVariable) return false;
    return backendVariable.startsWith("R_C_") || backendVariable.startsWith("S_C_");
};

/**
 * Get Concept and Recall variants for a given row
 */
export const getConceptAndRecall = (rowDisplayLabel, rowBackendVariable, moduleName, kpis) => {
    const filteredBvObj = kpis.filter((ele) => ele.displayLabel === rowDisplayLabel);

    return {
        conceptBvObj: filteredBvObj.find((ele) => isConceptVariable(ele.backendVariable)) || {},
        recallBvObj: filteredBvObj.find((ele) => !isConceptVariable(ele.backendVariable)) || {},
    };
};

export const getProductColumnClassName = (productCodeTitle, productCodeCoreDataId, productColumnVisibility) => {
    const key = `checked--${productCodeTitle.split(" ").join("_")}__${productCodeCoreDataId}`;
    const hiddenClass = `checked_${productCodeTitle.replace(/\s+/g, "").toLowerCase()} hidden`;
    return !productColumnVisibility[key] ? key : hiddenClass;
};

export const isProductVisible = (productColumnVisibility, datum) => {
    const classNameKey = `checked--${datum.title.split(" ").join("_")}__${datum.coreDataId}`;
    return !productColumnVisibility[classNameKey];
};

export const getScoreByVariable = (scores = [], variable = "") => scores.find((scoreObj) => scoreObj.backendVariable === variable) || {};


export const isLabelOrSiblingRemoved = (kpis = [], bvObj, removedKpiStoreState = [], moduleName) => {
    if (moduleName === "claims") {
        return removedKpiStoreState.includes(bvObj.backendVariable);
    }
    const removedSet = new Set(removedKpiStoreState.flatMap((item) => item.split(",")));
    return kpis.some((k) => k.displayLabel === bvObj.displayLabel && removedSet.has(k.backendVariable));
};
export const getProductScoreSumObject = ({ catObj, box, key1, key2, key3 = null, isITMDataShown, moduleName, draggableColumns, productColumnVisibility, isConceptRecallShown = false, }) => {
    const kpis = catObj?.displayLabels || [];
    const productScoreTotalObj = {};
    const allScoresFlat = draggableColumns?.flatMap((d) => d.scores || []) || [];
    kpis.forEach((bvObj) => {
        const rowDisplayLabel = bvObj.displayLabel;
        const rowBackendVariable = bvObj.backendVariable;
        let itmTopOrMeanKey1 = 0;
        let itmbottomKey2 = 0;
        let recallTopOrMeanKey1 = 0;
        let recallbottomKey2 = 0;
        let recallThirdKey3 = 0;
        let conceptTopOrMeanKey1 = 0;
        let conceptbottomKey2 = 0;
        let conceptThirdKey3 = 0;
        let topPenaltySum = 0;
        let bottomPenaltySum = 0;
        let penaltyCount = 0;

        const { recallBvObj, itmBvObj } = getRecallAndITM(rowDisplayLabel, rowBackendVariable, moduleName, kpis);
        const { conceptBvObj, recallBvObj: conceptRecallBvObj } = getConceptAndRecall(rowDisplayLabel, rowBackendVariable, moduleName, kpis, allScoresFlat);

        draggableColumns?.forEach((datum) => {
            let recallScore = null;
            let conceptScore = null;
            let itmBvScore = null;
            if (moduleName === "overall") {
                // recallScore = datum.scores.filter((scoreObj) => scoreObj.backendVariable === recallBvObj?.backendVariable)?.[0] || {};
                recallScore = getScoreByVariable(datum.scores, recallBvObj?.backendVariable);
                if (isITMDataShown) {
                    // itmBvScore = datum.scores.filter((scoreObj) => scoreObj.backendVariable === itmBvObj?.backendVariable)?.[0] || {};
                    itmBvScore = getScoreByVariable(datum.scores, itmBvObj?.backendVariable);
                }
                // Concept score for hair care overall
                if (conceptBvObj?.backendVariable) {
                    conceptScore = getScoreByVariable(datum.scores, conceptBvObj.backendVariable);
                }
            } else {
                const filteredBvObjScore = kpis.filter((ele) => ele.displayLabel === rowDisplayLabel)?.[0] || {};
                recallScore = getScoreByVariable(datum.scores, filteredBvObjScore?.backendVariable);
                // recallScore = datum.scores.filter((scoreObj) => scoreObj.backendVariable === filteredBvObjScore?.backendVariable)?.[0] || {};
                if (conceptBvObj?.backendVariable) {
                    conceptScore = getScoreByVariable(datum.scores, conceptBvObj.backendVariable);
                }
            }

            if (isConceptRecallShown && conceptRecallBvObj?.backendVariable) {
                recallScore = getScoreByVariable(datum.scores, conceptRecallBvObj.backendVariable);
            }

            if (!isProductVisible(productColumnVisibility, datum)) return;

            const sampleSize = parseInt(datum.sampleSize) || 0;

            if (isITMDataShown && moduleName === "overall") {
                if (box) {
                    const _itmTopOrMeanKey1 = Number(itmBvScore?.[key1] || 0);
                    const _itmbottomKey2 = Number(itmBvScore?.[key2] || 0);
                    const _recallTopOrMeanKey1 = Number(recallScore?.[key1] || 0);
                    const _recallbottomKey2 = Number(recallScore?.[key2] || 0);

                    itmTopOrMeanKey1 += getCustomRound((_itmTopOrMeanKey1 * sampleSize) / 100);
                    itmbottomKey2 += getCustomRound((_itmbottomKey2 * sampleSize) / 100);
                    recallTopOrMeanKey1 += getCustomRound((_recallTopOrMeanKey1 * sampleSize) / 100);
                    recallbottomKey2 += getCustomRound((_recallbottomKey2 * sampleSize) / 100);
                } else {
                    itmTopOrMeanKey1 += Number(itmBvScore?.[key1] || 0);
                    recallTopOrMeanKey1 += Number(recallScore?.[key1] || 0);
                }
            } else if (box || moduleName === "justRight") {
                const _recallTopOrMeanKey1 = Number(recallScore?.[key1] || 0);
                const _recallbottomKey2 = Number(recallScore?.[key2] || 0);
                const _recallThirdKey3 = Number(recallScore?.[key3] || 0);

                recallTopOrMeanKey1 += getCustomRound((_recallTopOrMeanKey1 * sampleSize) / 100);
                recallbottomKey2 += getCustomRound((_recallbottomKey2 * sampleSize) / 100);
                recallThirdKey3 += getCustomRound((_recallThirdKey3 * sampleSize) / 100);
                const _rawTopP = recallScore?.bottom_penalty;
                const _rawBottomP = recallScore?.top_penalty;
                const _topPenalty = (_rawTopP === null || _rawTopP === undefined || _rawTopP === "NaN" || isNaN(Number(_rawTopP))) ? null : Number(_rawTopP);
                const _bottomPenalty = (_rawBottomP === null || _rawBottomP === undefined || _rawBottomP === "NaN" || isNaN(Number(_rawBottomP))) ? null : Number(_rawBottomP);
                if (_topPenalty !== null || _bottomPenalty !== null) {
                    topPenaltySum += _topPenalty;
                    bottomPenaltySum += _bottomPenalty;
                    penaltyCount += 1;
                }
            } else {
                recallTopOrMeanKey1 += Number(recallScore?.[key1] || 0);
            }

            // Concept overall aggregation (same logic as recall, using concept score)
            if (conceptScore) {
                if (box) {
                    const _conceptTopOrMeanKey1 = Number(conceptScore?.[key1] || 0);
                    const _conceptbottomKey2 = Number(conceptScore?.[key2] || 0);
                    const _conceptThirdKey3 = Number(conceptScore?.[key3] || 0);
                    conceptTopOrMeanKey1 += getCustomRound((_conceptTopOrMeanKey1 * sampleSize) / 100);
                    conceptbottomKey2 += getCustomRound((_conceptbottomKey2 * sampleSize) / 100);
                    conceptThirdKey3 += getCustomRound((_conceptThirdKey3 * sampleSize) / 100);
                } else {
                    conceptTopOrMeanKey1 += Number(conceptScore?.[key1] || 0);
                }
            }
        });

        productScoreTotalObj[rowDisplayLabel] = {
            itmTopOrMeanKey1: Number(itmTopOrMeanKey1.toFixed(2)),
            itmbottomKey2: Number(itmbottomKey2.toFixed(2)),
            recallTopOrMeanKey1: Number(recallTopOrMeanKey1.toFixed(2)),
            recallbottomKey2: Number(recallbottomKey2.toFixed(2)),
            recallThirdKey3: Number(recallThirdKey3.toFixed(2)),
            conceptTopOrMeanKey1: Number(conceptTopOrMeanKey1.toFixed(2)),
            conceptbottomKey2: Number(conceptbottomKey2.toFixed(2)),
            conceptThirdKey3: Number(conceptThirdKey3.toFixed(2)),
            topPenaltyAvg: penaltyCount > 0 ? Number((topPenaltySum / penaltyCount).toFixed(2)) : 0,
            bottomPenaltyAvg: penaltyCount > 0 ? Number((bottomPenaltySum / penaltyCount).toFixed(2)) : 0,
        };
    });

    return productScoreTotalObj;
};

export const getProductScoreWithNaAndRowSpan = ({ catObj, box, key1, key2, key3 = null, isITMDataShown, moduleName, draggableColumns, noOfProducts, removedKpiStoreState, isConceptRecallShown = false, isConceptDataShown = false, isHairCare = false }) => {
    const kpis = catObj?.displayLabels || [];
    let rowSpan = kpis.length;
    const productScoreWithNaObj = {};
    // const productScoreTotalObj = {}

    const uniqueKpis = new Set();
    kpis.forEach((bvObj) => {
        const rowDisplayLabel = bvObj.displayLabel;
        const rowBackendVariable = bvObj.backendVariable;
        if (!uniqueKpis.has(bvObj.displayLabel)) {
            const { recallBvObj, itmBvObj } = getRecallAndITM(rowDisplayLabel, rowBackendVariable, moduleName, kpis);
            const { conceptBvObj, recallBvObj: conceptRecallBvObj } = getConceptAndRecall(rowDisplayLabel, rowBackendVariable, moduleName, kpis);

            let productsScoreWithNA = 0;
            draggableColumns?.forEach((datum) => {
                let recallScore = null;
                let itmBvScore = null;
                if (moduleName === "overall") {
                    if (isConceptDataShown && conceptBvObj?.backendVariable) {
                        recallScore = getScoreByVariable(datum.scores, conceptBvObj.backendVariable);
                    } else {
                        recallScore = getScoreByVariable(datum.scores, recallBvObj?.backendVariable);
                    }

                    if (isITMDataShown) {
                        // itmBvScore = datum.scores.filter((scoreObj) => scoreObj.backendVariable === itmBvObj?.backendVariable)?.[0] || {};
                        itmBvScore = getScoreByVariable(datum.scores, itmBvObj?.backendVariable);
                    }
                } else if (isConceptDataShown && conceptBvObj?.backendVariable) {
                    // For non-overall modules with Concept Data selected, use the Concept BV directly
                    recallScore = getScoreByVariable(datum.scores, conceptBvObj.backendVariable);
                } else {
                    const filteredBvObjScore = kpis.filter((ele) => ele.displayLabel === rowDisplayLabel)?.[0] || {};
                    recallScore = getScoreByVariable(datum.scores, filteredBvObjScore?.backendVariable);
                }


                if (isConceptRecallShown && !isConceptDataShown && conceptRecallBvObj?.backendVariable) {
                    recallScore = getScoreByVariable(datum.scores, conceptRecallBvObj.backendVariable);
                }

                const conceptScoreForNA = isHairCare && !isConceptDataShown && conceptBvObj?.backendVariable
                    ? getScoreByVariable(datum.scores, conceptBvObj.backendVariable)
                    : null;
                const isConceptNA = !conceptScoreForNA
                    || (box
                        ? (!conceptScoreForNA[key1] && !conceptScoreForNA[key2] && (!key3 || !conceptScoreForNA[key3]))
                        : !conceptScoreForNA[key1]);

                if (isITMDataShown && moduleName === "overall") {
                    const isITMNA = !itmBvScore?.[key1] && !itmBvScore?.[key2];
                    const isRecallNA = !recallScore?.[key1] && !recallScore?.[key2];

                    if (box && isITMNA && isRecallNA) {
                        productsScoreWithNA += 1;
                    } else if (!box && !itmBvScore?.[key1] && !recallScore?.[key1]) {
                        productsScoreWithNA += 1;
                    }
                } else {
                    const isRecallNA = !recallScore?.[key1] && !recallScore?.[key2] && (!key3 || !recallScore?.[key3]);
                    if (box && isRecallNA && isConceptNA) {
                        productsScoreWithNA += 1;
                    } else if (!box && !recallScore?.[key1] && isConceptNA) {
                        productsScoreWithNA += 1;
                    }
                }
            });
            if (productsScoreWithNA === noOfProducts) {
                productScoreWithNaObj[rowDisplayLabel] = productsScoreWithNA;
                rowSpan -= 1;
            } else {
                const isRemoved = isLabelOrSiblingRemoved(kpis, bvObj, removedKpiStoreState, moduleName);
                uniqueKpis.add(bvObj.displayLabel);
                if (isRemoved) {
                    rowSpan -= 1;
                }
            }
        } else {
            rowSpan -= 1;
        }
    });

    if (isHairCare && moduleName !== "overall" && moduleName !== "claims") {
        kpis.forEach((bvObj) => {
            const rowBackendVariable = bvObj.backendVariable;
            // When Concept Data is selected, only Concept BVs can have data — mark Recall BVs as all-NA
            if (isConceptDataShown && !isConceptVariable(rowBackendVariable, bvObj.variableCategory)) {
                productScoreWithNaObj[rowBackendVariable] = noOfProducts;
                return;
            }
            // When Recall Data is selected, only Recall BVs can have data — mark Concept BVs as all-NA
            if (!isConceptDataShown && isConceptVariable(rowBackendVariable, bvObj.variableCategory) && !isConceptRecallShown) {
                productScoreWithNaObj[rowBackendVariable] = noOfProducts;
                return;
            }
            let productsScoreWithNA = 0;
            draggableColumns?.forEach((datum) => {
                const score = getScoreByVariable(datum.scores, rowBackendVariable);
                const isNA = !score?.[key1] && !score?.[key2] && (!key3 || !score?.[key3]);
                if (box ? isNA : !score?.[key1]) {
                    productsScoreWithNA += 1;
                }
            });
            productScoreWithNaObj[rowBackendVariable] = productsScoreWithNA;
        });
    }

    return {
        rowSpan: rowSpan,
        productScoreWithNaObj: productScoreWithNaObj,
    };
};

export const getProductScoreWithNa = ({ catObj, box, key1, key2, key3 = null, isITMDataShown, moduleName, productsSecondaryData, noOfProducts }) => {
    const kpis = catObj?.displayLabels || [];
    let productScoreWithNaObj = {};
    // const productScoreTotalObj = {}
    const uniqueKpis = new Set();
    kpis.forEach((bvObj) => {
        const rowDisplayLabel = bvObj.displayLabel;
        const rowBackendVariable = bvObj.backendVariable;
        if (!uniqueKpis.has(bvObj.displayLabel)) {
            const { recallBvObj, itmBvObj } = getRecallAndITM(rowDisplayLabel, rowBackendVariable, moduleName, kpis);

            let productsScoreWithNA = 0;
            productsSecondaryData?.forEach((datum) => {
                let recallScore = null;
                let itmBvScore = null;
                if (moduleName === "overall") {
                    // recallScore = datum.scores.filter((scoreObj) => scoreObj.backendVariable === recallBvObj?.backendVariable)?.[0] || {};
                    recallScore = getScoreByVariable(datum.scores, recallBvObj?.backendVariable);

                    if (isITMDataShown) {
                        // itmBvScore = datum.scores.filter((scoreObj) => scoreObj.backendVariable === itmBvObj?.backendVariable)?.[0] || {};
                        itmBvScore = getScoreByVariable(datum.scores, itmBvObj?.backendVariable);
                    }
                } else {
                    const filteredBvObjScore = kpis.filter((ele) => ele.displayLabel === rowDisplayLabel)?.[0] || {};
                    recallScore = getScoreByVariable(datum.scores, filteredBvObjScore?.backendVariable);
                    // recallScore = datum.scores.filter((scoreObj) => scoreObj.backendVariable === filteredBvObjScore?.backendVariable)?.[0] || {};
                }
                if (isITMDataShown && moduleName === "overall") {
                    const isITMNA = !itmBvScore?.[key1] && !itmBvScore?.[key2];
                    const isRecallNA = !recallScore?.[key1] && !recallScore?.[key2];

                    if (box && isITMNA && isRecallNA) {
                        productsScoreWithNA += 1;
                    } else if (!box && !itmBvScore?.[key1] && !recallScore?.[key1]) {
                        productsScoreWithNA += 1;
                    }
                } else {
                    const isRecallNA = !recallScore?.[key1] && !recallScore?.[key2] && (!key3 || !recallScore?.[key3]);
                    if (box && isRecallNA) {
                        productsScoreWithNA += 1;
                    } else if (!box && !recallScore?.[key1]) {
                        productsScoreWithNA += 1;
                    }
                }
            });
            if (productsScoreWithNA === noOfProducts) {
                productScoreWithNaObj[rowDisplayLabel] = productsScoreWithNA;
            } else {
                uniqueKpis.add(bvObj.displayLabel);
            }
        }
    });

    return {
        productScoreWithNaObj: productScoreWithNaObj,
    };
};

export const gapRow = ({ length, hideCount, box, boxAnalysisTypeSelection, isITMDataShown, overallToggle, dataViewKey, targetColumnIndex, hasActuallyMoved }) => {
    const tds = [];
    // this is for 1st column
    tds.push(
        <td
            style={{
                height: "5px",
                padding: 0,
                position: "sticky",
                left: 0,
                backgroundColor: "#ffffff",
                zIndex: 5,
            }}
        >
            &nbsp;
        </td>
    );
    // this is for 2nd column
    tds.push(
        <td
            style={{
                height: "5px",
                padding: 0,
                position: "sticky",
                left: 200,
                backgroundColor: "#ffffff",
                zIndex: 5,
            }}
        >
            &nbsp;
        </td>
    );

    if (box) {
        boxAnalysisTypeSelection.map(() => {
            if (isITMDataShown) {
                tds.push(
                    <td
                        style={{
                            height: "5px",
                            padding: 0,
                        }}
                    >
                        &nbsp;
                    </td>
                );
            }
            tds.push(
                <td
                    style={{
                        height: "5px",
                        padding: 0,
                    }}
                >
                    &nbsp;
                </td>
            );
        });
    }

    {
        isITMDataShown &&
            overallToggle &&
            dataViewKey.split(",").map(() => {
                tds.push(
                    <td
                        style={{
                            height: "5px",
                            padding: 0,
                        }}
                    >
                        &nbsp;
                    </td>
                );
            });
    }

    {
        overallToggle &&
            dataViewKey.split(",").map(() => {
                tds.push(
                    <td
                        style={{
                            height: "5px",
                            padding: 0,
                        }}
                    >
                        &nbsp;
                    </td>
                );
            });
    }

    {
        for (let i = 0; i < length - hideCount; i++) {
            {
                isITMDataShown &&
                    dataViewKey.split(",").map((ele, i) => {
                        tds.push(
                            <td
                                style={{
                                    backgroundColor: targetColumnIndex === i && hasActuallyMoved ? "#d8eeff" : "",
                                    height: "5px",
                                    padding: 0,
                                }}
                            >
                                &nbsp;
                            </td>
                        );
                    });
            }
            {
                dataViewKey.split(",").map((ele, i) => {
                    tds.push(
                        <td
                            style={{
                                backgroundColor: targetColumnIndex === i && hasActuallyMoved ? "#d8eeff" : "",
                                height: "5px",
                                padding: 0,
                            }}
                        >
                            &nbsp;
                        </td>
                    );
                });
            }
        }
    }
    return tds;
};

// functions for chart view

function hslToRgb(h, s, l) {
    // Normalize inputs
    h = ((h % 360) + 360) % 360; // wrap to [0,360)
    s = Math.max(0, Math.min(100, s)) / 100;
    l = Math.max(0, Math.min(100, l)) / 100;

    const c = (1 - Math.abs(2 * l - 1)) * s; // chroma
    const hh = h / 60;
    const x = c * (1 - Math.abs((hh % 2) - 1));
    let r1 = 0,
        g1 = 0,
        b1 = 0;

    if (0 <= hh && hh < 1) {
        r1 = c;
        g1 = x;
        b1 = 0;
    } else if (1 <= hh && hh < 2) {
        r1 = x;
        g1 = c;
        b1 = 0;
    } else if (2 <= hh && hh < 3) {
        r1 = 0;
        g1 = c;
        b1 = x;
    } else if (3 <= hh && hh < 4) {
        r1 = 0;
        g1 = x;
        b1 = c;
    } else if (4 <= hh && hh < 5) {
        r1 = x;
        g1 = 0;
        b1 = c;
    } else if (5 <= hh && hh < 6) {
        r1 = c;
        g1 = 0;
        b1 = x;
    }

    const m = l - c / 2;
    const r = Math.round((r1 + m) * 255);
    const g = Math.round((g1 + m) * 255);
    const b = Math.round((b1 + m) * 255);

    return [r, g, b];
}

export function hslToCss(h, s, l, alpha) {
    const [r, g, b] = hslToRgb(h, s, l);
    if (typeof alpha === "number") {
        const a = Math.max(0, Math.min(1, alpha));
        return `rgba(${r}, ${g}, ${b}, ${a})`;
    }
    return `rgb(${r}, ${g}, ${b})`;
}

export const testResultFlipLabels = ["Top Box", "Bottom Box"];

function getProductScoreWithNA(activeKpis, key1, key2, draggableColumns, kpiBackendVariable, kpiDisplayLabel, moduleName, box, isITMDataShown) {
    const { recallBvObj, itmBvObj } = getRecallAndITM(kpiDisplayLabel, kpiBackendVariable, moduleName, activeKpis);
    const { conceptBvObj } = getConceptAndRecall(kpiDisplayLabel, kpiBackendVariable, moduleName, activeKpis);
    const isConceptBv = isConceptVariable(kpiBackendVariable);
    let productsScoreWithNA = 0;
    draggableColumns.forEach((datum) => {
        let recallScore = null;
        let itmBvScore = null;
        if (moduleName === "overall") {
            if (isConceptBv) {
                recallScore = getScoreByVariable(datum.scores, kpiBackendVariable);
            } else if (conceptBvObj?.backendVariable && !recallBvObj?.backendVariable) {
                recallScore = getScoreByVariable(datum.scores, conceptBvObj.backendVariable);
            } else {
                recallScore = getScoreByVariable(datum.scores, recallBvObj?.backendVariable);
            }

            if (isITMDataShown) {
                // itmBvScore = datum.scores.filter((scoreObj) => scoreObj.backendVariable === itmBvObj?.backendVariable)?.[0] || {};
                itmBvScore = getScoreByVariable(datum.scores, itmBvObj?.backendVariable);
            }
        } else {
            const filteredBvObjScore = activeKpis.filter((ele) => ele.displayLabel === kpiDisplayLabel)?.[0] || {};
            recallScore = getScoreByVariable(datum.scores, filteredBvObjScore?.backendVariable);
            // recallScore = datum.scores.filter((scoreObj) => scoreObj.backendVariable === filteredBvObjScore?.backendVariable)?.[0] || {};
        }
        if (isITMDataShown && moduleName === "overall") {
            const isITMNA = !itmBvScore?.[key1] && !itmBvScore?.[key2];
            const isRecallNA = !recallScore?.[key1] && !recallScore?.[key2];

            if (box && isITMNA && isRecallNA) {
                productsScoreWithNA += 1;
            } else if (!box && !itmBvScore?.[key1] && !recallScore?.[key1]) {
                productsScoreWithNA += 1;
            }
        } else {
            const isRecallNA = !recallScore?.[key1] && !recallScore?.[key2];
            if (box && isRecallNA) {
                productsScoreWithNA += 1;
            } else if (!box && !recallScore?.[key1]) {
                productsScoreWithNA += 1;
            }
        }
    });

    return productsScoreWithNA;
}

function getEndLabel(activeKpis, variable) {
    const inRecall = activeKpis.filter((ele) => ele.variableCategory === "KPI (OO)")?.some((item) => item.displayLabel === variable);
    const inItm = activeKpis.filter((ele) => ele.variableCategory === "KPI ITM")?.some((item) => item.displayLabel === variable);
    const inConcept = activeKpis.filter((ele) => isConceptVariable(ele.backendVariable))?.some((item) => item.displayLabel === variable);

    if (inRecall && !inItm) {
        return "(Recall)";
    } else if (!inRecall && inItm) {
        return "(ITM)";
    } else if (inConcept) {
        return "(Concept)";
    }
    else {
        return "";
    }
}

export const getChartProductsDataBeautyAndWellbeing = (productScoreWithNaObj, activeKpis, key1, key2, key3, draggableColumns, dataViewKey, box, noOfProducts, removedKpiStoreState, tTestResult, likertScaleData, moduleName, isITMDataShown, testResultFlip, itmToggle, isJar = false, selectedStudy = [], actionStandardKpis = [], isConceptDataShown = false,) => {
    let _products = {};
    let _rich = [];
    let _variableVisibilityKpis = [];

    const _kpisMap = new Map();
    if (moduleName === "overall") {
        activeKpis.forEach((bvObj) => {
            const kpiDisplayLabel = bvObj.displayLabel;
            const kpiBackendVariable = bvObj.backendVariable;
            const productsScoreWithNA = getProductScoreWithNA(activeKpis, key1, key2, draggableColumns, kpiBackendVariable, kpiDisplayLabel, moduleName, box, itmToggle);

            if (productsScoreWithNA < noOfProducts) {
                const datum = {
                    ...bvObj,
                };
                const endLabel = getEndLabel(activeKpis, kpiDisplayLabel);
                if (likertScaleData?.[kpiBackendVariable]) {
                    const kpiWithLikertScale = `${kpiDisplayLabel} (${likertScaleData[bvObj.backendVariable]}) ${endLabel}`;
                    datum["displayLabel"] = kpiWithLikertScale;
                    _variableVisibilityKpis.push(datum);
                } else {
                    datum["displayLabel"] = `${kpiDisplayLabel} ${endLabel}`;
                    _variableVisibilityKpis.push(datum);
                }
            }
        });
    } else {
        activeKpis.forEach((bvObj) => {
            const kpiDisplayLabel = bvObj.displayLabel;
            const kpiBackendVariable = bvObj.backendVariable;
            let productsScoreWithNA = 0;
            if (productScoreWithNaObj[kpiBackendVariable] !== undefined) {
                productsScoreWithNA = productScoreWithNaObj[kpiBackendVariable];
            } else if (productScoreWithNaObj[kpiDisplayLabel] !== undefined) {
                productsScoreWithNA = productScoreWithNaObj[kpiDisplayLabel];
            }

            if (productsScoreWithNA < noOfProducts) {
                const datum = {
                    ...bvObj,
                };
                if (likertScaleData?.[kpiBackendVariable] && moduleName !== "claims") {
                    const kpiWithLikertScale = `${kpiDisplayLabel} (${likertScaleData[bvObj.backendVariable]})`;
                    datum["displayLabel"] = kpiWithLikertScale;
                    _variableVisibilityKpis.push(datum);
                } else {
                    datum["displayLabel"] = kpiDisplayLabel;
                    _variableVisibilityKpis.push(datum);
                }
            }
        });
    }
    if (moduleName === "overall") {
        if (isITMDataShown) {
            activeKpis = activeKpis.filter((ele) => ele.variableCategory === "KPI ITM");
        } else if (isConceptDataShown) {
            activeKpis = activeKpis.filter((ele) => ele.variableCategory === "Concept KPI (OO)");
        } else {
            activeKpis = activeKpis.filter((ele) => ele.variableCategory === "KPI (OO)");
        }
    }

    activeKpis.forEach((bvObj) => {
        const kpiDisplayLabel = bvObj.displayLabel;
        const kpiBackendVariable = bvObj.backendVariable;

        // For Hair Care non-overall: use per-BV NA count to avoid dropping KPIs that share displayLabel
        const productsScoreWithNA = productScoreWithNaObj[kpiBackendVariable] !== undefined
            ? productScoreWithNaObj[kpiBackendVariable]
            : productScoreWithNaObj[kpiDisplayLabel];
        if ((!productsScoreWithNA || productsScoreWithNA < noOfProducts) && !removedKpiStoreState.flatMap((item) => item.split(",")).includes(kpiBackendVariable)) {
            draggableColumns.forEach((datum) => {
                const filteredBvObjScore = activeKpis.filter((ele) => ele.backendVariable === kpiBackendVariable)?.[0] || {};

                const _score = getScoreByVariable(datum.scores, filteredBvObjScore?.backendVariable);
                const _productName = datum.title.split(" ").join("_") + "__" + datum.coreDataId;

                if (!_.isEmpty(_score)) {
                    // Round percentage values for chart; preserve null/undefined as null (NA)
                    const getRoundedScore = (key) => {
                        const raw = _score[key];
                        if (raw === null || raw === undefined || raw === "NA") return null;
                        return dataViewKey.includes('per') || dataViewKey.includes('percent') ? getCustomRound(Number(raw)) : raw;
                    };

                    if (isJar && key3) {
                        // JAR: build 3 separate arrays + penalties
                        if (!Object.keys(_products).includes(_productName)) {
                            _products[_productName] = { coreDataID: datum.coreDataId, tooLittle: [], justRight: [], tooMuch: [], tooLittlePenalty: [], tooMuchPenalty: [] };
                        }
                        const tooLittlePct = getRoundedScore(key1);
                        const justRightPct = getRoundedScore(key2);
                        const tooMuchPct = getRoundedScore(key3);
                        const tooLittlePenalty = Number(_score.bottom_penalty);
                        const tooMuchPenalty = Number(_score.top_penalty);

                        _products[_productName].tooLittle.push(tooLittlePct);
                        _products[_productName].justRight.push(justRightPct);
                        _products[_productName].tooMuch.push(tooMuchPct);
                        _products[_productName].tooLittlePenalty.push(tooLittlePenalty);
                        _products[_productName].tooMuchPenalty.push(tooMuchPenalty);
                    } else {
                        // Existing logic
                        const key1Arr = key1.split("_");
                        const key1score = getRoundedScore(key1)

                        if (!Object.keys(_products).includes(_productName)) {
                            _products[_productName] = { coreDataID: datum.coreDataId };
                            _products[_productName][key1Arr[box ? 0 : key1Arr.length - 1]] = [];
                            if (key2) {
                                const key2Arr = key2.split("_");
                                _products[_productName][key2Arr[0]] = [];
                            }
                        }
                        _products[_productName][key1Arr[box ? 0 : key1Arr.length - 1]].push(key1score);
                        if (key2) {
                            const key2Arr = key2.split("_");
                            _products[_productName][key2Arr[0]].push(getRoundedScore(key2));
                        }
                    }

                    if (!_kpisMap.has(kpiBackendVariable)) {
                        let label = kpiDisplayLabel;

                        if (likertScaleData?.[kpiBackendVariable] && moduleName !== "claims") {
                            label = `${kpiDisplayLabel} (${likertScaleData[kpiBackendVariable]})`;
                        }

                        _kpisMap.set(kpiBackendVariable, {
                            backendVariable: kpiBackendVariable,
                            displayLabel: label,
                        });
                    }
                } else {
                    // NA values
                    if (isJar && key3) {
                        if (!Object.keys(_products).includes(_productName)) {
                            _products[_productName] = { coreDataID: datum.coreDataId, tooLittle: [], justRight: [], tooMuch: [], tooLittlePenalty: [], tooMuchPenalty: [] };
                        }
                        _products[_productName].tooLittle.push(null);
                        _products[_productName].justRight.push(null);
                        _products[_productName].tooMuch.push(null);
                        _products[_productName].tooLittlePenalty.push(null);
                        _products[_productName].tooMuchPenalty.push(null);
                    } else {
                        const key1Arr = key1.split("_");
                        if (!Object.keys(_products).includes(_productName)) {
                            _products[_productName] = { coreDataID: datum.coreDataId };
                            _products[_productName][key1Arr[box ? 0 : key1Arr.length - 1]] = [];
                            if (key2) {
                                const key2Arr = key2.split("_");
                                _products[_productName][key2Arr[0]] = [];
                            }
                        }
                        _products[_productName][key1Arr[box ? 0 : key1Arr.length - 1]].push(null);
                        if (key2) {
                            const key2Arr = key2.split("_");
                            _products[_productName][key2Arr[0]].push(null);
                        }
                    }
                }
            });
            let richObj = {
                padding: [12, 10],
                color: "black",
            };
            if (actionStandardKpis.includes(kpiBackendVariable) && selectedStudy.length === 1) {
                richObj = {
                    padding: [12, 10],
                    color: "black",
                    fontWeight: "900",
                    fontSize: "14px",
                };
            }
            if (tTestResult.length > 0) {
                const findByBv = tTestResult.find((item) => item.backendVariable === kpiBackendVariable);
                const checkOrdinary = tTestResult.some(ele => ele?.colors?.some(color => Object.keys(color).includes("ordinary") && color?.ordinary !== null))
                let key = "common";
                if (box && testResultFlip === testResultFlipLabels[1] && checkOrdinary) {
                    key = "ordinary";
                }
                if (!_.isEmpty(findByBv)) {
                    _rich.push({
                        ...richObj,
                        backgroundColor: findByBv?.colors[0][key] === "NA" ? "" : findByBv?.colors[0][key],
                    });
                } else {
                    _rich.push({
                        ...richObj,
                    });
                }
            } else {
                _rich.push({
                    ...richObj,
                });
            }
        }
    });

    return {
        _products: _products,
        _kpis: Array.from(_kpisMap.values()),
        _rich: _rich,
        _variableVisibilityKpis: _variableVisibilityKpis,
    };
};

export const getChartProductsData = (productScoreWithNaObj, activeKpis, key1, key2, draggableColumns, dataViewKey, box, noOfProducts, removedKpiStoreState, tTestResult, likertScaleData, moduleName, isITMDataShown, testResultFlip, itmToggle, absoluteToggle = false, selectedStudy = [], actionStandardKpis = [], isConceptDataShown = false, isRecallDataShown = false) => {
    let _products = {};
    let _rich = [];
    let _variableVisibilityKpis = [];

    const _kpisMap = new Map();
    if (moduleName === "overall") {
        activeKpis.forEach((bvObj) => {
            const kpiDisplayLabel = bvObj.displayLabel;
            const kpiBackendVariable = bvObj.backendVariable;
            const productsScoreWithNA = getProductScoreWithNA(activeKpis, key1, key2, draggableColumns, kpiBackendVariable, kpiDisplayLabel, moduleName, box, itmToggle);

            if (productsScoreWithNA < noOfProducts) {
                const datum = {
                    ...bvObj,
                };
                const endLabel = getEndLabel(activeKpis, kpiDisplayLabel);
                if (likertScaleData?.[kpiBackendVariable]) {
                    const kpiWithLikertScale = `${kpiDisplayLabel} (${likertScaleData[bvObj.backendVariable]}) ${endLabel}`;
                    datum["displayLabel"] = kpiWithLikertScale;
                    _variableVisibilityKpis.push(datum);
                } else {
                    datum["displayLabel"] = `${kpiDisplayLabel} ${endLabel}`;
                    _variableVisibilityKpis.push(datum);
                }
            }
        });
    } else {
        activeKpis.forEach((bvObj) => {
            const kpiDisplayLabel = bvObj.displayLabel;
            const kpiBackendVariable = bvObj.backendVariable;
            const productsScoreWithNA = productScoreWithNaObj[kpiDisplayLabel];

            if (!productsScoreWithNA || productsScoreWithNA < noOfProducts) {
                const datum = {
                    ...bvObj,
                };
                if (likertScaleData?.[kpiBackendVariable] && moduleName !== "claims") {
                    const kpiWithLikertScale = `${kpiDisplayLabel} (${likertScaleData[bvObj.backendVariable]})`;
                    datum["displayLabel"] = kpiWithLikertScale;
                    _variableVisibilityKpis.push(datum);
                } else {
                    datum["displayLabel"] = kpiDisplayLabel;
                    _variableVisibilityKpis.push(datum);
                }
            }
        });
    }
    if (moduleName === "overall") {
        if (isITMDataShown) {
            activeKpis = activeKpis.filter((ele) => ele.variableCategory === "KPI ITM");
        } else if (isConceptDataShown) {
            activeKpis = activeKpis.filter((ele) => ele.variableCategory === "Concept KPI (OO)");
        } else if (isRecallDataShown) {
            activeKpis = activeKpis.filter((ele) => ele.variableCategory === "KPI (OO)");
        } else {
            activeKpis = activeKpis.filter((ele) => ele.variableCategory === "KPI (OO)");
        }
    }

    activeKpis.forEach((bvObj) => {
        const kpiDisplayLabel = bvObj.displayLabel;
        const kpiBackendVariable = bvObj.backendVariable;

        const productsScoreWithNA = productScoreWithNaObj[kpiDisplayLabel];
        if ((!productsScoreWithNA || productsScoreWithNA < noOfProducts) && !removedKpiStoreState.flatMap((item) => item.split(",")).includes(kpiBackendVariable)) {
            draggableColumns.forEach((datum) => {
                const filteredBvObjScore = activeKpis.filter((ele) => ele.backendVariable === kpiBackendVariable)?.[0] || {};

                const _score = getScoreByVariable(datum.scores, filteredBvObjScore?.backendVariable);
                const key1Arr = key1.split("_");
                const _productName = datum.title.split(" ").join("_") + "__" + datum.coreDataId;
                if (!_.isEmpty(_score)) {
                    const getRoundedScore = (key) => dataViewKey.includes('middle') || dataViewKey.includes('percent') ? getCustomRound(Number(_score[key] ?? 0)) : (_score[key] ?? 0);

                    const initialText = key1Arr[0];
                    const key1score = absoluteToggle && box ? _score[initialText + "_absolute_count"] : getRoundedScore(key1);
                    // const key1score = box || dataViewKey.includes("middle") || dataViewKey.includes("percent") ? getCustomRound(Number(_score[key1] ?? 0)) : _score[key1] ?? 0;
                    // console.log("check -",_productName,filteredBvObjScore?.backendVariable,key1score)
                    if (!Object.keys(_products).includes(_productName)) {
                        _products[_productName] = {};
                        _products[_productName]["coreDataID"] = datum.coreDataId;
                        _products[_productName][key1Arr[box ? 0 : key1Arr.length - 1]] = [];
                        _products[_productName][key1Arr[box ? 0 : key1Arr.length - 1]].push(key1score);
                        if (key2) {
                            const key2Arr = key2.split("_");
                            const initialText2 = key2Arr[0];
                            const key2score = absoluteToggle && box ? _score[initialText2 + "_absolute_count"] : getRoundedScore(key2);
                            _products[_productName][key2Arr[0]] = [];
                            _products[_productName][key2Arr[0]].push(key2score);
                        }
                    } else {
                        _products[_productName][key1Arr[box ? 0 : key1Arr.length - 1]].push(key1score);
                        if (key2) {
                            const key2Arr = key2.split("_");
                            _products[_productName][key2Arr[0]].push(getCustomRound(Number(_score[key2] || 0)));
                        }
                    }

                    if (!_kpisMap.has(kpiBackendVariable)) {
                        let label = kpiDisplayLabel;

                        if (likertScaleData?.[kpiBackendVariable] && moduleName !== "claims") {
                            label = `${kpiDisplayLabel} (${likertScaleData[kpiBackendVariable]})`;
                        }

                        _kpisMap.set(kpiBackendVariable, {
                            backendVariable: kpiBackendVariable,
                            displayLabel: label,
                        });
                    }
                } else {
                    if (!Object.keys(_products).includes(_productName)) {
                        _products[_productName] = {};
                        _products[_productName]["coreDataID"] = datum.coreDataId;
                        _products[_productName][key1Arr[box ? 0 : key1Arr.length - 1]] = [];
                        _products[_productName][key1Arr[box ? 0 : key1Arr.length - 1]].push(0);
                        if (key2) {
                            const key2Arr = key2.split("_");
                            _products[_productName][key2Arr[0]] = [];
                            _products[_productName][key2Arr[0]].push(0);
                        }
                    } else {
                        _products[_productName][key1Arr[box ? 0 : key1Arr.length - 1]].push(0);
                        if (key2) {
                            const key2Arr = key2.split("_");
                            _products[_productName][key2Arr[0]].push(0);
                        }
                    }
                }
            });
            let richObj = {
                padding: [12, 10],
                color: "black",
            };
            if (actionStandardKpis.includes(kpiBackendVariable) && selectedStudy.length === 1) {
                richObj = {
                    padding: [12, 10],
                    color: "black",
                    fontWeight: "900",
                    fontSize: "14px",
                };
            }
            if (tTestResult.length > 0) {
                const findByBv = tTestResult.find((item) => item.backendVariable === kpiBackendVariable);
                const checkOrdinary = tTestResult.some(ele => ele?.colors?.some(color => Object.keys(color).includes("ordinary") && color?.ordinary !== null))
                let key = "common";
                if (box && testResultFlip === testResultFlipLabels[1] && checkOrdinary) {
                    key = "ordinary";
                }
                if (!_.isEmpty(findByBv)) {
                    _rich.push({
                        ...richObj,
                        backgroundColor: findByBv?.colors[0][key] === "NA" ? "" : findByBv?.colors[0][key],
                    });
                } else {
                    _rich.push({
                        ...richObj,
                    });
                }
            } else {
                _rich.push({
                    ...richObj,
                });
            }
        }
    });

    return {
        _products: _products,
        _kpis: Array.from(_kpisMap.values()),
        _rich: _rich,
        _variableVisibilityKpis: _variableVisibilityKpis,
    };
};
export const getPreferenceAnalysisChartProductsData = (productScoreWithNaObj, activeKpis, key1, key2, draggableColumns, dataViewKey, box, noOfProducts, removedKpiStoreState, tTestResult, likertScaleData, testResultFlip, selectedStudy = [], actionStandardKpis = []) => {
    let _products = {};
    let _rich = [];
    let _variableVisibilityKpis = [];

    const _kpisMap = new Map();

    activeKpis.forEach((bvObj) => {
        const kpiDisplayLabel = bvObj.displayLabel;
        const kpiBackendVariable = bvObj.backendVariable;
        const productsScoreWithNA = productScoreWithNaObj[kpiDisplayLabel];

        if (!productsScoreWithNA || productsScoreWithNA < noOfProducts) {
            const datum = {
                ...bvObj,
            };
            let kpiWithLikertScale = `${kpiDisplayLabel}`;
            if (likertScaleData?.[kpiBackendVariable]) {
                kpiWithLikertScale += `(${likertScaleData[bvObj.backendVariable]})`;
            }
            datum["displayLabel"] = kpiWithLikertScale;
            _variableVisibilityKpis.push(datum);
        }
    });

    activeKpis.forEach((bvObj) => {
        const kpiDisplayLabel = bvObj.displayLabel;
        const kpiBackendVariable = bvObj.backendVariable;

        const productsScoreWithNA = productScoreWithNaObj[kpiDisplayLabel];
        if ((!productsScoreWithNA || productsScoreWithNA < noOfProducts) && !removedKpiStoreState.flatMap((item) => item.split(",")).includes(kpiBackendVariable)) {
            draggableColumns.forEach((datum) => {
                const filteredBvObjScore = activeKpis.filter((ele) => ele.backendVariable === kpiBackendVariable)?.[0] || {};
                const _score = getScoreByVariable(datum.scores, filteredBvObjScore?.backendVariable);

                const _productName = datum.title.split(" ").join("_") + "__" + datum.coreDataId;
                const key1Arr = key1.split("_");
                const key1score = box || dataViewKey.includes("middle") || dataViewKey.includes("percent") ? getCustomRound(Number(_score[key1] ?? 0)) : _score[key1] ?? 0;
                if (!Object.keys(_products).includes(_productName)) {
                    _products[_productName] = {};
                    _products[_productName]["coreDataID"] = datum.coreDataId;
                    _products[_productName][key1Arr[box ? 0 : key1Arr.length - 1]] = [];
                    _products[_productName][key1Arr[box ? 0 : key1Arr.length - 1]].push(key1score);
                } else {
                    _products[_productName][key1Arr[box ? 0 : key1Arr.length - 1]].push(key1score);
                    if (key2) {
                        const key2Arr = key2.split("_");
                        _products[_productName][key2Arr[0]].push(getCustomRound(Number(_score[key2] || 0)));
                    }
                }

                if (!_kpisMap.has(kpiBackendVariable)) {
                    let label = kpiDisplayLabel;

                    if (likertScaleData?.[kpiBackendVariable]) {
                        label = `${kpiDisplayLabel} (${likertScaleData[kpiBackendVariable]})`;
                    }

                    _kpisMap.set(kpiBackendVariable, {
                        backendVariable: kpiBackendVariable,
                        displayLabel: label,
                    });
                }
            });
            let richObj = {
                padding: [12, 10],
                color: "black",
            };
            if (actionStandardKpis.includes(kpiBackendVariable) && selectedStudy.length === 1) {
                richObj = {
                    padding: [12, 10],
                    color: "black",
                    fontWeight: "900",
                    fontSize: "14px",
                };
            }
            if (tTestResult.length > 0) {
                const findByBv = tTestResult.find((item) => item.backendVariable === kpiBackendVariable);
                let key = "common";
                if (box && testResultFlip === testResultFlipLabels[1] && tTestResult[0].colors[0].ordinary) {
                    key = "ordinary";
                }
                if (!_.isEmpty(findByBv)) {
                    _rich.push({
                        ...richObj,
                        backgroundColor: findByBv?.colors[0][key],
                    });
                } else {
                    _rich.push({
                        ...richObj,
                    });
                }
            } else {
                _rich.push({
                    ...richObj,
                });
            }
        }
    });


    return {
        _products: _products,
        _kpis: Array.from(_kpisMap.values()),
        _rich: _rich,
        _variableVisibilityKpis: _variableVisibilityKpis,
    };
};

export const getYAxisMaxValue = (value) => {
    if (value <= 5) {
        return 5; // If value <= 5, return 5
    } else if (value > 5 && value <= 7) {
        return 7;
    } else if (value > 7 && value <= 10) {
        return 10;
    } else if (value > 10 && value <= 20) {
        return 20;
    } else if (value > 20 && value <= 50) {
        return 50;
    } else if (value > 50 && value <= 100) {
        return 100;
    } else if (value > 100) {
        return value;
    }
};

export const getFragranceYAxisMaxValue = (value, tTestResult) => {
    if (value <= 5) {
        return tTestResult.length && value == 5 ? parseInt(value) + 1 : 5; // If value <= 5, return 5
    } else if (value > 5 && value <= 7) {
        return tTestResult.length && value == 7 ? parseInt(value) + 1 : 7;
    } else if (value > 7 && value <= 10) {
        return tTestResult.length && value == 10 ? parseInt(value) + 2 : 10;
    } else if (value > 10 && value <= 20) {
        return tTestResult.length && value == 20 ? parseInt(value) + 5 : 20;
    } else if (value > 20 && value <= 50) {
        return tTestResult.length && value == 50 ? parseInt(value) + 10 : 50;
    } else if (value > 50 && value <= 100) {
        return tTestResult.length && value == 100 ? parseInt(value) + 10 : 100;
    } else if (value > 100) {
        return tTestResult.length ? parseInt(value) + 10 : value;
    }
};

export const getXAxisMaxValue = (value) => {
    if (value <= 5) {
        return 5; // If value <= 5, return 5
    } else if (value > 5 && value <= 7) {
        return 7;
    } else if (value > 7 && value <= 10) {
        return 10;
    } else if (value > 10 && value <= 20) {
        return 20;
    } else if (value > 20 && value <= 30) {
        return 30;
    } else if (value > 30 && value <= 40) {
        return 40;
    } else if (value > 40 && value <= 50) {
        return 50;
    } else if (value > 50 && value <= 60) {
        return 60;
    } else if (value > 60 && value <= 70) {
        return 70;
    } else if (value > 70 && value <= 80) {
        return 80;
    } else if (value > 80 && value <= 90) {
        return 90;
    } else if (value > 90 && value <= 100) {
        return 100;
    } else if (value > 100) {
        return value;
    }
};

const extractProductTitle = (productKey) => {
    const productTitleArr = productKey.split("__");
    let title = "";
    if (productTitleArr.length > 2) {
        // Product contains __ in its name (e.g., "Prototype 1 – __something__")
        title = productTitleArr.slice(0, productTitleArr.length - 1).join("__");
    } else {
        title = productTitleArr[0];
    }
    return title.replace(/_/g, " ");
};

export const getChartSeries = (products, key1, key2, box, analysisTypeValue, productColors, productColumnVisibility, isJar = false, tTestProducts = null, isBeautyAndWellbeing = false) => {
    let _yAxixMaxValue = 7;
    let seriesValueArr = [];
    const _series = [];
    const tTestProductsSet = tTestProducts?.products ?? tTestProducts;
    const isStudentOrZTest = tTestProducts?.isStudentOrZTest ?? false;
    const tTestProductsArr = tTestProductsSet ? Array.from(tTestProductsSet) : [];
    const referenceProduct = isStudentOrZTest && tTestProductsArr.length >= 2 ? tTestProductsArr[1] : null;
    if (isJar) {
        // JAR: 3 stacked bars per product
        const jarLabels = ['Too Little', 'Just Right', 'Too Much'];
        Object.keys(products).length > 0 &&
            Object.entries(products).forEach(([product, values], i) => {
                if (!productColumnVisibility[product]) {
                    const productTitle = extractProductTitle(product);
                    const isGreyed = tTestProductsSet && tTestProductsSet.size > 0 && !tTestProductsSet.has(productTitle);
                    const isReference = referenceProduct && productTitle === referenceProduct;
                    const jarData = [values.tooLittle, values.justRight, values.tooMuch];
                    let maxJarValue = 0;

                    jarLabels.forEach((jarLabel, jarIndex) => {
                        const jarValues = jarData[jarIndex] || [];
                        const jarPenalties = jarIndex === 0 ? values.tooLittlePenalty : (jarIndex === 2 ? values.tooMuchPenalty : []);
                        const jarDataWithPenalty = jarValues.map((val, kpiIndex) => ({
                            value: val,
                            penalty: jarPenalties[kpiIndex],
                        }));
                        const jarColor = isBeautyAndWellbeing && isGreyed ? "rgba(176, 176, 176, 0.3)" : isBeautyAndWellbeing && isReference ? "#808080" : hslToCss(...productColors[i], jarIndex === 0 ? 1 : jarIndex === 1 ? 0.6 : 0.3);
                        const productJarName = `${productTitle} - ${jarLabel}`;
                        _series.push({
                            name: productJarName,
                            type: 'bar',
                            stack: productTitle,
                            data: jarDataWithPenalty,
                            itemStyle: { color: jarColor },
                            label: {
                                show: false,
                            },
                        });
                        maxJarValue = Math.max(maxJarValue, ...jarValues);
                    });
                    seriesValueArr.push(...jarData.flat());
                    _yAxixMaxValue = Math.max(_yAxixMaxValue, 100); // JAR always max 100%
                }
            });
    } else if (key1) {
        // Existing logic for non-JAR
        const key1Arr = key1.split("_");
        Object.keys(products).length > 0 &&
            Object.entries(products).forEach(([product, values], i) => {
                if (!productColumnVisibility[product]) {
                    const productTitle = extractProductTitle(product);

                    let productName = "";
                    const isGreyed = tTestProductsSet && tTestProductsSet.size > 0 && !tTestProductsSet.has(productTitle);
                    const isReference = referenceProduct && productTitle === referenceProduct;
                    if (box) {
                        productName = `${productTitle} - ${key1Arr[0]}`;
                    } else {
                        productName = productTitle;
                    }

                    if (key1 && !key2) {
                        seriesValueArr.push(...values[key1Arr[box ? 0 : key1Arr.length - 1]]);
                    }

                    if (key2) {
                        const productTitle2 = extractProductTitle(product);
                        const color2 = isBeautyAndWellbeing && isGreyed ? "rgba(176, 176, 176, 0.3)" : isBeautyAndWellbeing && isReference ? "#808080" : hslToCss(...productColors[i], 0.4);
                        _series.push({
                            name: `${productTitle2} - bottom`,
                            type: box ? "bar" : "line",
                            stack: product,
                            data: values.bottom,
                            itemStyle: { color: color2 },
                            label: {
                                show: false,
                                position: ["10", "5"],
                                formatter: (params) => (params.value ? params.value + "%" : ""),
                            },
                        });
                        const sumTopBottom = values.top.map((value, index) => Number(value) + Number(values.bottom[index]));
                        seriesValueArr.push(...sumTopBottom);
                    }
                    const color1 = isBeautyAndWellbeing && isGreyed ? "rgba(176, 176, 176, 0.3)" : isBeautyAndWellbeing && isReference ? "#808080" : hslToCss(...productColors[i]);
                    _series.push({
                        name: productName,
                        type: box || analysisTypeValue.value === "middle" ? "bar" : "line",
                        stack: product,
                        data: values[key1Arr[box ? 0 : key1Arr.length - 1]],
                        itemStyle: { color: color1 },
                        label: {
                            show: false,
                            position: ["10", "5"],
                            formatter: (params) => (box ? `${params.value ? params.value + "%" : ""}` : params.value),
                        },
                    });
                }
            });
        _yAxixMaxValue = getYAxisMaxValue(Math.max(...seriesValueArr));
    }

    return {
        _series: _series,
        _yAxixMaxValue: _yAxixMaxValue,
    };
};

export const getAgreementChartSeries = (products, key1, key2, box, productColors, productColumnVisibility) => {
    let _yAxixMaxValue = 7;
    let seriesValueArr = [];
    const _series = [];
    if (key1) {
        const key1Arr = key1.split("_");
        Object.keys(products).length > 0 &&
            Object.entries(products).forEach(([product, values], i) => {
                if (!productColumnVisibility[product]) {
                    let productName = "";
                    const productTitle = product.split("__")[0].replace(/_/g, " ");
                    if (box) {
                        productName = `${productTitle} - ${key1Arr[0]}`;
                    } else {
                        productName = productTitle;
                    }
                    if (key1 && !key2) {
                        seriesValueArr.push(...values[key1Arr[box ? 0 : key1Arr.length - 1]]);
                    }

                    _yAxixMaxValue = getYAxisMaxValue(Math.max(...values[key1Arr[0]].map(Number)));
                    if (key2) {
                        const productTitle2 = product.split("__")[0].replace(/_/g, " ");
                        const color2 = hslToCss(...productColors[i], 0.4);
                        _series.push({
                            name: `${productTitle2} - bottom`,
                            type: "bar",
                            stack: product,
                            data: values.bottom,
                            itemStyle: { color: color2 },
                            label: {
                                show: false,
                                position: ["10", "5"],
                                formatter: (params) => (params.value ? params.value + "%" : ""),
                            },
                        });
                        const sumTopBottom = values.top.map((value, index) => value + values.bottom[index]);
                        seriesValueArr.push(...sumTopBottom);
                    }
                    const color1 = hslToCss(...productColors[i]);
                    _series.push({
                        name: productName,
                        type: "bar",
                        stack: product,
                        data: values[key1Arr[0]],
                        itemStyle: { color: color1 },
                        label: {
                            show: false,
                            position: ["10", "5"],
                            formatter: (params) => `${params.value ? params.value + "%" : ""}`,
                        },
                    });
                }
            });
        _yAxixMaxValue = getYAxisMaxValue(Math.max(...seriesValueArr));
    }

    return {
        _series: _series,
        _yAxixMaxValue: _yAxixMaxValue,
    };
};

function makeWhiskerData(series) {
    let out = [];
    for (let i = 0; i < series.length; i++) {
        const v = series[i];
        const half = 0.5;
        if (v === "0.00" || v === "0" || v === 0) {
            out.push([i, null, null, null]);
        } else {
            out.push([i, v, v - half, parseFloat(v) + half]);
        }
    }

    return out;
}

export const getFragranceChartSeries = (products, key1, key2, box, productColors, tTestResult, productColumnVisibility) => {
    let _yAxixMaxValue = 5;
    const _series = [];
    const countProd = Object.entries(products)
        .map(([product]) => {
            if (!productColumnVisibility[`checked--${product.split(" ").join("_")}`]) {
                return product;
            }
        })
        .filter((ele) => ele !== undefined);
    const N = countProd.length;

    if (key1) {
        const key1Arr = key1.split("_");
        let pIndex = 0;
        Object.keys(products).length > 0 &&
            Object.entries(products).forEach(([product, values], i) => {
                const visibilityKey = `checked--${product.split(" ").join("_")}`;
                let productName = "";
                if (box) {
                    productName = `${product} - ${key1Arr[0]}`;
                } else {
                    productName = product;
                }
                const maxProductValue = getFragranceYAxisMaxValue(Math.max(...values[key1Arr[box ? 0 : key1Arr.length - 1]].map(Number)), tTestResult);
                _yAxixMaxValue = _yAxixMaxValue < maxProductValue ? maxProductValue : _yAxixMaxValue;
                if (key2) {
                    const color2 = hslToCss(...productColors[i], 0.4);
                    _series.push({
                        name: `${product} - bottom`,
                        type: "bar",
                        stack: product,
                        data: values.bottom,
                        itemStyle: { color: color2 },
                        label: {
                            show: false,
                            position: "top",
                            fontWeight: "bold",
                            padding: 5,
                            formatter: (params) => (params.value ? params.value + "%" : ""),
                        },
                    });
                    const sumTopBottom = values.top.map((value, index) => value + values.bottom[index]);
                    _yAxixMaxValue = getFragranceYAxisMaxValue(Math.max(...sumTopBottom), tTestResult);
                }
                const color1 = hslToCss(...productColors[i]);
                _series.push({
                    name: productName,
                    type: "bar",
                    stack: product,
                    data: values[key1Arr[box ? 0 : key1Arr.length - 1]],
                    itemStyle: { color: color1 },
                    label: {
                        show: true,
                        position: "top",
                        fontWeight: "bold",
                        padding: 10,
                        formatter: (params) => (box ? `${params.value ? params.value + "%" : ""}` : `${params.value ? params.value : ""}`),
                    },
                });

                if (tTestResult.length) {
                    if (!productColumnVisibility[visibilityKey]) {
                        const calcByIndex = (pIndex - (N - 1) / 2)
                        _series.push({
                            type: "custom",
                            data: makeWhiskerData(values[key1Arr[0]]),
                            itemStyle: { color: color1 },
                            clip: true,
                            renderItem: function (params, api) {
                                const index = api.value(0);
                                const val = api.value(1);

                                const band = params.coordSys.width / values[key1Arr[0]].length;
                                const offset = N === 1 ? 0 : calcByIndex * (band / (N * 1.25)); // left bar

                                const pCenter = api.coord([index, val]);
                                const x = pCenter[0] + offset;

                                const cap = 6;

                                return {
                                    type: "group",
                                    children: [
                                        { type: "line", shape: { x1: x, y1: pCenter[1] - 10, x2: x, y2: pCenter[1] + 10 }, style: { stroke: "#000" } },
                                        { type: "line", shape: { x1: x - cap, y1: pCenter[1] - 10, x2: x + cap, y2: pCenter[1] - 10 }, style: { stroke: "#000" } },
                                        { type: "line", shape: { x1: x - cap, y1: pCenter[1] + 10, x2: x + cap, y2: pCenter[1] + 10 }, style: { stroke: "#000" } },
                                    ],
                                };
                            },
                            z: 10,
                        });
                        ++pIndex;
                    }
                }
            });
    }
    return {
        _series: _series,
        _yAxixMaxValue: _yAxixMaxValue,
    };
};

export const getPreferenceChartSeries = (products, key1, percentageToggle, productColors, respondentData, kpis, productColumnVisibility) => {
    let _xAxixMaxValue = 7;
    // let sumProducts = 0;
    let resArr = [];
    const _series = [];
    const respondentArr = kpis.length && kpis.map((kpi) => respondentData[kpi.backendVariable]).filter((v) => v !== undefined);

    if (key1) {
        const key1Arr = key1.split("_");
        Object.keys(products).length > 0 &&
            Object.entries(products).forEach(([product, values], i) => {
                const visibilityKey = `checked--${product.split(" ").join("_")}__${values.coreDataID}`;
                // sumProducts += Math.max(...values[key1Arr[percentageToggle ? 0 : key1Arr.length - 1]]);
                if (!productColumnVisibility?.[visibilityKey]) {
                    resArr = values[key1Arr[percentageToggle ? 0 : key1Arr.length - 1]].map((value, index) => value + (resArr[index] ?? 0));
                }
                const color1 = hslToCss(...productColors[i]);
                _series.push({
                    name: product,
                    type: "bar",
                    stack: "product",
                    data: values[key1Arr[percentageToggle ? 0 : key1Arr.length - 1]],
                    itemStyle: { color: color1 },
                    barWidth: 100,
                    label: {
                        show: true,
                        position: "insideRight",
                        fontWeight: "bold",
                        formatter: (params) => (percentageToggle ? `${params.value >= 5 ? params.value + "%" : ""}` : `${params.value >= 2 ? params.value : ""}`),
                    },
                });
            });
        _series.push({
            name: "",
            type: "bar",
            silent: true,
            barGap: "-100%",
            barWidth: 100,
            data: resArr,
            itemStyle: { color: "transparent" },
            label: {
                show: true,
                position: "right",
                padding: 10,
                color: "#000",
                formatter: (params) => respondentArr?.[params.dataIndex],
            },
        });
        // console.log("_series",_series,resArr,respondentArr)
        // const customSeries = {
        //     type: "custom",
        //     renderItem: (params, api) => {
        //         const categoryIndex = api.value(0);



        //         const total = resArr[categoryIndex];

        //         // Position at end of stacked bar
        //         const point = api.coord([total, categoryIndex]);

        //         return {
        //             type: "text",

        //             style: {
        //                 text: `${total}`,
        //                 x: point[0] + 10,
        //                 y: point[1] + 5,
        //                 fill: "#333",
        //             },
        //         };
        //     },

        //     data: resArr.map((_, i) => [i]),

        //     z: 100,
        // }
        // _series.push(customSeries);
        _xAxixMaxValue = getXAxisMaxValue(Math.max(...resArr));
        // console.log("_yAxixMaxValue",_yAxixMaxValue)
    }

    return {
        _series: _series,
        _xAxixMaxValue: _xAxixMaxValue,
    };
};

export const getDataView = (analysisTypeValue, weightToggle, boxAnalysisTypeSelection) => {
    const { value } = analysisTypeValue;
    let dataViewKey = "mean"; // Default

    if (value === "mean") {
        dataViewKey = weightToggle ? "weight_mean" : "mean";
    } else if (value === "middle") {
        dataViewKey = weightToggle ? "middle_weight_per" : "middle_per";
    } else if (value === "count" || value === "percentage") {
        dataViewKey = value;
    } else if (value === "allThreeBox") {
        dataViewKey = weightToggle ? "bottom_weight_per,middle_weight_per,top_weight_per" : "bottom_per,middle_per,top_per";
    } else {
        const hasTopBox = boxAnalysisTypeSelection.includes("Top Box");
        const hasBottomBox = boxAnalysisTypeSelection.includes("Bottom Box");

        if (hasTopBox && hasBottomBox) {
            dataViewKey = weightToggle ? "top_weight_per,bottom_weight_per" : "top_per,bottom_per";
        } else if (hasTopBox) {
            dataViewKey = weightToggle ? "top_weight_per" : "top_per";
        } else if (hasBottomBox) {
            dataViewKey = weightToggle ? "bottom_weight_per" : "bottom_per";
        }
    }
    return dataViewKey;
};

export const getSortedDataMapping = (mappings, customOrderMacroCategory, moduleName, applyBackendVariableSortOrder, isUnileverFragranceHouse = false) => {
    let sortedData = [];

    if (moduleName !== "claims" && customOrderMacroCategory) {
        sortedData = mappings.slice().sort((a, b) => {
            const indexA = getSortIndex(a.macroCategory, customOrderMacroCategory);
            const indexB = getSortIndex(b.macroCategory, customOrderMacroCategory);

            return indexA - indexB;
        });
    } else if (moduleName === "claims") {
        const claimsData = mappings.map((item) => ({
            macroCategory: item.tags.join(","),
            displayLabels: [
                {
                    backendVariable: item.backendVariable,
                    displayLabel: item.statement,
                    variableCategory: "Claims",
                },
            ],
        }));

        sortedData = claimsData.sort((a, b) => {
            const labelA = a.displayLabels[0].displayLabel.toLowerCase();
            const labelB = b.displayLabels[0].displayLabel.toLowerCase();
            return labelA.localeCompare(labelB);
        });
    } else {
        sortedData = mappings;
    }
    //Sort displayLabels alphabetically by displayLabel
    sortedData = sortedData.map((item) => {
        let sortedLabels = item.displayLabels.slice();

        if (applyBackendVariableSortOrder) {
            let customOrder;

            if (moduleName === "agreement") {
                customOrder = MacroCategoryOrder?.[moduleName]?.customSubOrderSkinCleansing;
            } else if (moduleName === "overall" && !isUnileverFragranceHouse) {
                customOrder = MacroCategoryOrder?.[moduleName]?.customSubOrderOralCare;
            }  else if (moduleName === "overall" && isUnileverFragranceHouse) {
                customOrder = MacroCategoryOrder?.[moduleName]?.customSubOrderUnileverFragranceHouse;
            }

            if (customOrder) {
                sortedLabels = sortedLabels.sort((a, b) => {
                    const indexA = customOrder.indexOf(a.backendVariable);
                    const indexB = customOrder.indexOf(b.backendVariable);
                    return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
                });
            } else {
                // Default alphabetical sort
                sortedLabels = sortedLabels.sort((a, b) => a.displayLabel.localeCompare(b.displayLabel, undefined, { sensitivity: "base" }));
            }
        } else {
            // Default alphabetical sort
            sortedLabels = sortedLabels.sort((a, b) => a.displayLabel.localeCompare(b.displayLabel, undefined, { sensitivity: "base" }));
        }

        if (moduleName === "fragranceMetrics" || moduleName === "preference") {
            sortedLabels = sortedLabels.sort((a, b) => {
                const A = a.displayLabel.toLowerCase();
                const B = b.displayLabel.toLowerCase();

                const isImmA = A.includes("immediately");
                const isImmB = B.includes("immediately");
                if (isImmA !== isImmB) return isImmA ? -1 : 1;

                const numA = parseInt(A.match(/\d+/)?.[0] ?? Infinity, 10);
                const numB = parseInt(B.match(/\d+/)?.[0] ?? Infinity, 10);

                return numA - numB;
            });
        }

        return {
            ...item,
            displayLabels: sortedLabels,
        };
    });
    return sortedData;
};

export const getAlphabeticalMacroCategories = (arr = []) => {
    // Return macro categories sorted alphabetically
    if (arr.length > 0) {
        const sortedArr = arr.sort((a, b) => {
            const labelA = a.macroCategory.toLowerCase(); // Ignore case
            const labelB = b.macroCategory.toLowerCase(); // Ignore case
            if (labelA < labelB) {
                return -1;
            }
            if (labelA > labelB) {
                return 1;
            }
            return 0; // Labels are equal
        });
        return sortedArr.map((ele) => ele.macroCategory);
    }
};

export const getDynamicColor = (startColor, endColor, steps) => {
    let colorArray = ["#c7ddf7"];
    if (steps) {
        const interpolateColor = (start, end, factor) => Math.round(start + (end - start) * factor);

        const hex = (value) => value.toString(16).padStart(2, "0");

        const startRGB = {
            r: parseInt(startColor.slice(1, 3), 16),
            g: parseInt(startColor.slice(3, 5), 16),
            b: parseInt(startColor.slice(5, 7), 16),
        };
        const endRGB = {
            r: parseInt(endColor.slice(1, 3), 16),
            g: parseInt(endColor.slice(3, 5), 16),
            b: parseInt(endColor.slice(5, 7), 16),
        };

        colorArray = Array.from({ length: steps }, (_, i) => {
            const factor = i / (steps - 1);
            const r = interpolateColor(startRGB.r, endRGB.r, factor);
            const g = interpolateColor(startRGB.g, endRGB.g, factor);
            const b = interpolateColor(startRGB.b, endRGB.b, factor);
            return `#${hex(r)}${hex(g)}${hex(b)}`;
        });
    }

    return colorArray;
};

export const getGraphLabelProductNameWithTestcodes = (selectedStudy, studyReference, productTitle, productsTestCodeMapData) => {
    const coredataId = selectedStudy.find((study) => study.study_reference__title === studyReference)?.id;
    const proTitle = `${productTitle}__${coredataId}`;
    const proTestCode = productsTestCodeMapData[proTitle];

    if (proTestCode && !productTitle.includes(proTestCode)) {
        return `${productTitle} / ${proTestCode} / ${studyReference}`;
    } else {
        return `${productTitle} / ${studyReference}`;
    }
};

export const translateRemovedKpis = (removedList, allMappings, targetVariableCategories) => {
    if (!removedList?.length || !allMappings?.length || !targetVariableCategories?.length) return [];

    const allBvObjs = allMappings.flatMap((m) => m.displayLabels || []);

    // backendVariable → displayLabel (across ALL categories)
    const bvToLabel = {};
    allBvObjs.forEach((bvObj) => { bvToLabel[bvObj.backendVariable] = bvObj.displayLabel; });

    // displayLabel → backendVariables[] for target categories only
    const labelToTargetBvs = {};
    allBvObjs
        .filter((bvObj) => targetVariableCategories.includes(bvObj.variableCategory))
        .forEach((bvObj) => {
            if (!labelToTargetBvs[bvObj.displayLabel]) labelToTargetBvs[bvObj.displayLabel] = [];
            labelToTargetBvs[bvObj.displayLabel].push(bvObj.backendVariable);
        });

    const result = [];
    removedList.forEach((entry) => {
        const bvs = entry.split(",");
        // Collect all unique displayLabels for this entry
        const labels = [...new Set(bvs.map((bv) => bvToLabel[bv]).filter(Boolean))];
        labels.forEach((label) => {
            const targetBvs = labelToTargetBvs[label];
            if (targetBvs?.length) {
                const joined = targetBvs.join(",");
                if (!result.includes(joined)) result.push(joined);
            }
        });
    });
    return result;
};

export const checkOverallDataPresent = (draggableColumns, target) => {
    const overallExists = draggableColumns.some((datum) => Array.isArray(datum.scores) && datum.scores.some((score) => score.backendVariable === target));
    return overallExists;
};

export const getSortedDatabyOverallMeanScore = (draggableColumns, target) => {
    if (!Array.isArray(draggableColumns)) {
        return [];
    }
    const dataToSort = [...draggableColumns];

    dataToSort.sort((productA, productB) => {
        const scoreA = productA.scores?.find((score) => score.backendVariable.includes(target));
        const scoreB = productB.scores?.find((score) => score.backendVariable.includes(target));
        const meanA = scoreA ? parseFloat(scoreA.mean) : 0;
        const meanB = scoreB ? parseFloat(scoreB.mean) : 0;
        return meanB - meanA;
    });

    return dataToSort;
};

export const getSortedDataByUlAndCompetitor = (draggableColumns) => {
    if (!Array.isArray(draggableColumns)) {
        return [];
    }

    const dataToSort = [...draggableColumns];

    dataToSort.sort((productA, productB) => {
        //Unilever first (isCompetitorProduct = false)
        if (productA.isCompetitorProduct !== productB.isCompetitorProduct) {
            return productA.isCompetitorProduct ? 1 : -1;
        }
        //Alphabetical within same group
        const titleA = productA.title || "";
        const titleB = productB.title || "";

        return titleA.localeCompare(titleB, undefined, {
            sensitivity: "base",
        });
    });

    return dataToSort;
};

export const sortMultipleStudies = (draggableColumns, targetVariable) => {
    if (!Array.isArray(draggableColumns)) return [];
    const studiesMap = {};
    draggableColumns.forEach((product) => {
        const studyRef = product.study_reference_title;
        if (!studiesMap[studyRef]) studiesMap[studyRef] = [];
        studiesMap[studyRef].push(product);
    });

    const studiesWithFrag = [];
    const studiesWithoutFrag = [];
    Object.keys(studiesMap).forEach((studyRef) => {
        const products = studiesMap[studyRef];
        const hasFragranceLiking = products.some((product) => product.scores?.some((s) => s.backendVariable === targetVariable));
        if (hasFragranceLiking) {
            studiesWithFrag.push({ studyRef, products });
        } else {
            studiesWithoutFrag.push({ studyRef, products });
        }
    });

    const sortedResult = [];
    studiesWithFrag.forEach((group) => {
        const sortedProducts = getSortedDatabyOverallMeanScore(group.products, targetVariable);
        sortedResult.push(...sortedProducts);
    });

    studiesWithoutFrag.forEach((group) => {
        const sortedProducts = getSortedDataByUlAndCompetitor(group.products);
        sortedResult.push(...sortedProducts);
    });
    return sortedResult;
};
